import { createStart, createCsrfMiddleware, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
// Replaces the generated `attachSupabaseAuth` so the backend client stays out of
// the entry bundle; identical behaviour, just imported on demand.
const attachSupabaseAuth = createMiddleware({ type: "function" }).client(async ({ next }) => {
  // No persisted session marker means there is no token to attach, so skip
  // pulling the backend client into the page entirely (saves ~50 KiB on
  // public pages that call unauthenticated server functions).
  const { maybeHasSession } = await import("@/lib/session-hint");
  if (!maybeHasSession()) return next();
  const { getAccessToken } = await import("@/lib/supabase-lazy");
  const token = await getAccessToken();
  return next({ headers: token ? { Authorization: `Bearer ${token}` } : {} });
});

const errorMiddleware = createMiddleware().server(async ({ next, request }) => {
  if (new URL(request.url).pathname.startsWith("/lovable/")) {
    return next();
  }
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

// Start installs this automatically when src/start.ts is absent; defining the
// file opts out, so re-add it explicitly to keep server functions protected
// from cross-site requests.
const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) =>
    ctx.handlerType === "serverFn" && !new URL(ctx.request.url).pathname.startsWith("/lovable/"),
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuth],
  requestMiddleware: [errorMiddleware, csrfMiddleware],
}));
